import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import { useTranslationApp } from "../../lib/i18next";
import { VIEW_SPEND_BREAKDOWN } from "../../i18n/keysTranslations";
import { numberCompactFormat } from "../../utils/numbers";
import { BORDER_RADIUS } from "../../utils/constants";
import {
  BOX_SHADOW_ICON_BOX,
  GRADIENT_BLACK_COLOR_HOVER,
  INPUT_COLOR_BORDER,
} from "../../utils/colors";
import MetricSparkline from "./MetricSparkline";

const StatisticCard = ({
  label,
  value,
  currency,
  suffix,
  icon: IconComponent,
  iconSrc,
  platformBreakdown,
  isLoading = false,
  growth = null,
  previousValue = null,
  previousCurrency = null,
  showPreviousPeriod = false,
  previousPeriodLabel = "",
  changeText = null,
  hideChangeText = false,
  hideIcon = false,
  sparklineValues = null,
  sparklineTrend = "stable",
  valueFormatter = null,
  growthFormatter = null,
  sx = {},
}) => {
  const { t } = useTranslationApp();
  const showSparklineSection = Boolean(sparklineValues || isLoading);
  const showIcon = !hideIcon && !showSparklineSection;

  const renderValue = () => {
    if (isLoading) {
      return <Skeleton variant="text" width={80} height={32} />;
    }

    if (value === null || value === undefined) {
      return "-";
    }

    if (label?.toLowerCase().includes("roas")) {
      const roasValue =
        isNaN(value) || value === null ? 0 : Number(value).toFixed(2);
      return `${roasValue}x`;
    }

    if (value <= 0) {
      return "-";
    }

    if (valueFormatter) return valueFormatter(value);

    let formattedValue;
    if (currency) {
      formattedValue = `${numberCompactFormat(value)} ${currency}`;
    } else {
      formattedValue = `${numberCompactFormat(value)}${suffix || ""}`;
    }

    return formattedValue;
  };

  const renderGrowthChip = () => {
    if (isLoading) return <Skeleton variant="rounded" width={60} height={20} />;
    if (
      growth === null ||
      growth === undefined ||
      growth === 0 ||
      growth === "0.00" ||
      growth === "0"
    )
      return null;
    const chipColor =
      growth > 0 ? "#e6f4ea" : growth < 0 ? "#fce8e6" : "#f1f3f4";
    const textColor =
      growth > 0 ? "#137333" : growth < 0 ? "#a50e0e" : "#5f6368";

    return (
      <Chip
        size="small"
        label={
          growth === 0 ? (
            "--"
          ) : (
            <>
              {growth > 0 ? "↑ " : "↓ "}
              {growthFormatter
                ? growthFormatter(Math.abs(growth))
                : `${Math.abs(growth).toFixed(1)}%`}
            </>
          )
        }
        sx={{
          backgroundColor: chipColor,
          color: textColor,
          fontWeight: 600,
          fontSize: "0.75rem",
          height: 20,
          "& .MuiChip-label": {
            px: 1,
          },
        }}
      />
    );
  };

  const renderPreviousPeriod = () => {
    if (isLoading) return <Skeleton variant="text" width={120} height={16} />;
    if (
      !showPreviousPeriod ||
      previousValue === null ||
      previousValue === undefined ||
      previousValue === 0 ||
      previousValue === "0.00" ||
      previousValue === "0"
    ) {
      return null;
    }

    let formattedPreviousValue;
    if (label?.toLowerCase().includes("roas")) {
      formattedPreviousValue = `${isNaN(previousValue) || previousValue === null ? 0 : Number(previousValue).toFixed(2)}x`;
    } else if (previousCurrency) {
      formattedPreviousValue = `${numberCompactFormat(previousValue)} ${previousCurrency}`;
    } else {
      formattedPreviousValue = numberCompactFormat(previousValue);
    }

    return (
      <Typography
        variant="caption"
        sx={{
          color: "rgba(0, 0, 0, 0.6)",
          fontSize: "0.75rem",
          fontWeight: "normal",
          fontStyle: "italic",
        }}
      >
        {`${previousPeriodLabel}: ${formattedPreviousValue}`}
      </Typography>
    );
  };

  return (
    <Card
      sx={{
        border: `1px solid ${INPUT_COLOR_BORDER}`,
        borderRadius: BORDER_RADIUS,
        boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
        backgroundColor: "white",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          boxShadow:
            "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
          borderColor: "#e5e7eb",
        },
        ...sx,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "text.secondary",
                mb: 0.5,
              }}
            >
              {label}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "baseline",
                columnGap: 1.25,
                mb: 0.5,
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: "1.5rem",
                  fontWeight: 700,
                }}
              >
                {renderValue()}
              </Typography>
              {!isLoading && platformBreakdown?.length > 0 && (
                <Tooltip
                  placement="bottom-start"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        p: 0,
                        maxWidth: 320,
                        borderRadius: 3,
                        mt: "8px !important",
                        backgroundColor: "background.paper",
                        color: "text.primary",
                        border: `1px solid ${INPUT_COLOR_BORDER}`,
                      },
                    },
                  }}
                  title={
                    <Box
                      sx={{
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.75,
                      }}
                    >
                      {platformBreakdown.map((p, i) => (
                        <Box
                          key={i}
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {p.logoSrc && (
                            <Box
                              component="img"
                              src={p.logoSrc}
                              alt=""
                              sx={{
                                width: 14,
                                height: 14,
                                objectFit: "contain",
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <Typography
                            variant="body2"
                            sx={{ color: "text.primary" }}
                          >
                            {p.name}: {numberCompactFormat(p.amount)}
                            {currency ? ` ${currency}` : ""}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  }
                >
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      textDecoration: "underline",
                      textUnderlineOffset: "2px",
                      cursor: "default",
                      fontSize: "0.72rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t(VIEW_SPEND_BREAKDOWN)}
                  </Typography>
                </Tooltip>
              )}
            </Box>

            {changeText && !hideChangeText && (
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: "text.secondary",
                }}
              >
                {changeText}
              </Typography>
            )}

            {renderPreviousPeriod()}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 1,
            }}
          >
            {renderGrowthChip()}

            {showSparklineSection && (
              <MetricSparkline
                values={sparklineValues}
                trend={sparklineTrend}
                isLoading={isLoading}
              />
            )}

            {showIcon && (
              <Box
                className="icon-container"
                sx={{
                  width: 40,
                  height: 40,
                  background: GRADIENT_BLACK_COLOR_HOVER,
                  boxShadow: BOX_SHADOW_ICON_BOX,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "transform 0.2s ease-in-out",
                  ".group:hover &": {
                    transform: "scale(1.05)",
                  },
                }}
              >
                {iconSrc ? (
                  <Box
                    className="icon"
                    component="img"
                    src={iconSrc}
                    alt=""
                    sx={{
                      width: 22,
                      height: 22,
                      objectFit: "contain",
                      transition: "all 0.2s ease-in-out",
                    }}
                  />
                ) : (
                  IconComponent && (
                    <Box
                      className="icon"
                      component={IconComponent}
                      sx={{
                        width: 20,
                        height: 20,
                        color: "#FFFFFF !important",
                        transition: "all 0.2s ease-in-out",
                      }}
                    />
                  )
                )}
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default StatisticCard;
